/**
 * Client-side helpers for the Conversation Actions section: catalog CSV
 * pre-validation (fast feedback before the authoritative admin-API import),
 * template/catalog downloads, and the sample webhook payload viewer content.
 *
 * CSV spec: gcx-cloudbriz-toolbar docs/CONVERSATION_ACTIONS_PLAN.md Section 4 /
 * CONVERSATION_ACTIONS_WEBHOOK.md Section 9. Caps: 5 MB raw or 100,000 rows.
 */

export const CATALOG_MAX_BYTES = 5 * 1024 * 1024;
export const CATALOG_MAX_ROWS = 100_000;

export type CatalogPrecheck =
  | { ok: true; rowCount: number }
  | { ok: false; errorKey: string };

/** Magic bytes: .xlsx is a ZIP (PK\x03\x04); legacy .xls is CFB (D0 CF 11 E0). */
function looksLikeExcel(bytes: Uint8Array, fileName: string): boolean {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return true;
  if (bytes.length >= 4) {
    if (bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) return true;
    if (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) return true;
  }
  return false;
}

/** Split one CSV line on commas, honoring double-quoted fields. */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Fast client-side pre-parse of a catalog file. The admin API remains the
 * authoritative validator; this exists so obvious problems (Excel file, wrong
 * encoding, missing columns, oversize) fail instantly with a clear message.
 * Returned errorKey values are i18n keys (settings-actions-catalog-*).
 */
export async function precheckCatalogFile(file: File): Promise<CatalogPrecheck> {
  if (file.size > CATALOG_MAX_BYTES) {
    return { ok: false, errorKey: 'settings-actions-catalog-too-large' };
  }

  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (looksLikeExcel(bytes, file.name)) {
    return { ok: false, errorKey: 'settings-actions-catalog-xlsx' };
  }

  let text: string;
  try {
    // fatal: true rejects non-UTF-8 (legacy Windows-1255/1252 exports) instead
    // of importing mojibake. Excel's UTF-8 BOM is stripped below.
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return { ok: false, errorKey: 'settings-actions-catalog-encoding' };
  }
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }

  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { ok: false, errorKey: 'settings-actions-catalog-empty-file' };
  }

  const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  if (!header.includes('sku') || !header.includes('product_name')) {
    return { ok: false, errorKey: 'settings-actions-catalog-header' };
  }

  const rowCount = lines.length - 1;
  if (rowCount > CATALOG_MAX_ROWS) {
    return { ok: false, errorKey: 'settings-actions-catalog-too-many-rows' };
  }

  return { ok: true, rowCount };
}

// Example rows (admins replace them): a simple product, a product with
// several aliases + package_size, and an inactive row demonstrating `active`.
// Real customer files may use aliases in any language; the samples are English.
// Exported as a verification seam (round-trips against the admin-API parser).
export const TEMPLATE_CSV = [
  'sku,product_name,description,unit,package_size,aliases,active',
  'BEV-0100,Orange Juice 1L,Freshly squeezed,EA,,oj|orange juice,true',
  'DAIRY-0042,Whole Milk 3% 1L,Whole milk 1L bottle,CASE,12,milk|whole milk|milk 3%,true',
  'OLD-0001,Legacy Product,No longer sold,EA,,,false',
  '',
].join('\n');

function csvField(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Convert the active parsed catalog (S3 JSON products) back to spec CSV. */
export function catalogProductsToCsv(products: Array<Record<string, unknown>>): string {
  const rows = ['sku,product_name,description,unit,package_size,aliases,active'];
  for (const p of products) {
    const aliases = Array.isArray(p.aliases) ? (p.aliases as unknown[]).join('|') : '';
    rows.push([
      csvField(p.sku),
      csvField(p.productName ?? p.product_name),
      csvField(p.description),
      csvField(p.unit),
      csvField(p.packageSize ?? p.package_size),
      csvField(aliases),
      csvField(p.active === false ? 'false' : 'true'),
    ].join(','));
  }
  return `${rows.join('\n')}\n`;
}

/** Trigger a browser download of a generated text file. */
export function downloadTextFile(fileName: string, contents: string, mimeType: string): void {
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadTemplateCsv(): void {
  downloadTextFile('mediabriz-catalog-template.csv', TEMPLATE_CSV, 'text/csv;charset=utf-8');
}

/**
 * The exact schema-version-1 JSON a configured endpoint receives, with the
 * org/instance identity substituted in. Structure matches the webhook
 * integration spec (CONVERSATION_ACTIONS_WEBHOOK.md Section 2); sample values
 * are neutral English placeholders. `customFields` uses the action's own
 * configured keys when it has any, so the sample matches what the endpoint
 * will actually receive; example keys stand in until then.
 *
 * `CREATE_RETURN` shares the envelope (schemaVersion, action, organizationId,
 * conversationId, externalTag, metadata, customer, queue, customFields). Its items
 * add `reason` (damaged | expired | wrong_item | surplus | other | null) and
 * `reasonText`; the return carries `reference` and `pickupDate` instead of
 * `requestedDeliveryDate`.
 *
 * `purchaseOrderNumber` and `notes` left the payload on 2026-09-15 (the
 * backend omits them; a pre-6.5.94 toolbar's non-empty value is still
 * forwarded). The example custom fields carry a `notes` key instead, which is
 * how an org gets that text today.
 *
 * `SEND_FILES` keeps the envelope without `items`, `requestedDeliveryDate` and
 * `metadata.agentModifiedFields`, and adds `files` (toolbar
 * CONVERSATION_ACTIONS_WEBHOOK.md "File deliveries").
 */
export function buildSampleWebhookPayload(args: {
  type: string;
  organizationId: string;
  actionId: string;
  label: string;
  customFields?: Record<string, string>;
}): Record<string, unknown> {
  const isReturn = args.type === 'CREATE_RETURN';
  const isSendFiles = args.type === 'SEND_FILES';
  const customFields = args.customFields && Object.keys(args.customFields).length > 0
    ? args.customFields
    : isSendFiles
      ? { case_number: 'CASE-1042', document_type: 'invoice' }
      : { delivery_slot: 'Morning', warehouse: 'North', notes: 'Deliver to the back entrance' };
  const envelope = {
    schemaVersion: 1,
    eventId: '9c4f2a7e-5b3d-4e8f-9a1b-2c3d4e5f6a7b',
    action: {
      type: isReturn ? 'CREATE_RETURN' : 'CREATE_ORDER',
      actionId: args.actionId || 'act_8f3a1c2e',
      label: args.label || (isReturn ? 'Return - Dairy' : 'Order - Dairy'),
    },
    organizationId: args.organizationId || 'your-genesys-org-guid',
    conversationId: 'c0ffee00-1111-2222-3333-444455556666',
    externalTag: 'TCK-58214',
    metadata: {
      agent: { id: 'agent-user-guid', name: 'Alex Morgan', email: 'alex.morgan@example.com' },
      agentModifiedFields: [isReturn ? 'items[0].reason' : 'items[0].quantity'],
      submittedAt: '2026-08-29T11:20:33.000Z',
      source: 'mediabriz-agent',
    },
    customer: {
      externalContactId: 'b1e2c3d4-5f6a-7b8c-9d0e-1f2a3b4c5d6e',
      name: 'Jane Smith',
      channelType: 'whatsapp',
      channelAddress: '15555550123',
      customerReference: 'ERP-CUST-1188',
    },
  };
  if (isSendFiles) {
    const { agentModifiedFields: _fields, ...metadata } = envelope.metadata;
    return {
      ...envelope,
      action: {
        type: 'SEND_FILES',
        actionId: args.actionId || 'act_8a1e2f3c',
        label: args.label || 'Save to CRM',
      },
      metadata,
      // Agents enter no customer number when they send files: always null on a real delivery.
      customer: { ...envelope.customer, customerReference: null },
      queue: { id: 'q-guid', name: 'Support' },
      files: [
        {
          mediaId: 'a1b2c3d4-5e6f-4a7b-8c9d-0e1f2a3b4c5d',
          fileName: 'invoice.pdf',
          contentType: 'application/pdf',
          sizeBytes: 184320,
          sourceMessageId: 'msg-guid',
          senderParty: 'customer',
          downloadUrl: 'https://api.mediabriz.com/prod/actions/files/<token>',
        },
      ],
      customFields,
    };
  }
  if (isReturn) {
    return {
      ...envelope,
      items: [
        {
          sku: 'DAIRY-0001',
          productName: 'Whole Milk 3% 1L',
          quantity: 2,
          unit: 'CASE',
          originalDescription: '2 cases of milk arrived leaking',
          reason: 'damaged',
          reasonText: 'the crates came leaking',
        },
      ],
      queue: { id: 'q-guid', name: 'Returns' },
      reference: 'DN-2026-04471',
      pickupDate: '2026-09-02',
      customFields,
    };
  }
  return {
    ...envelope,
    items: [
      {
        sku: 'DAIRY-0001',
        productName: 'Whole Milk 3% 1L',
        quantity: 3,
        unit: 'CASE',
        originalDescription: '3 cases of whole milk',
      },
    ],
    queue: { id: 'q-guid', name: 'Orders' },
    requestedDeliveryDate: '2026-09-01',
    customFields,
  };
}
